import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import UserLayout from '../../components/user/UserLayout';
import { Button } from '../../components/ui/button';
import { Plus, User, Edit2, Trash2, ExternalLink } from 'lucide-react';
import { apiRequest } from '../../utils/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

export default function UserDigitalProfiles() {
  const navigate = useNavigate();
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [maxProfiles, setMaxProfiles] = useState(1);
  const [canCreateMore, setCanCreateMore] = useState(true);

  useEffect(() => {
    fetchProfiles();
  }, []);

  const fetchProfiles = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await apiRequest(`${BACKEND_URL}/api/user/digital-profiles`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        setProfiles(response.data.profiles || []);
        setMaxProfiles(response.data.max_profiles);
        setCanCreateMore(response.data.can_create_more);
      }
      setLoading(false);
    } catch (err) {
      console.error('Failed to fetch profiles:', err);
      setLoading(false);
    }
  };

  const handleDelete = async (profileId) => {
    if (!window.confirm('Are you sure you want to delete this profile?')) return;

    try {
      const token = localStorage.getItem('auth_token');
      const response = await apiRequest(`${BACKEND_URL}/api/user/digital-profiles/${profileId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        fetchProfiles();
      } else {
        alert('Failed to delete profile');
      }
    } catch (err) {
      alert('Error deleting profile');
    }
  };

  return (
    <UserLayout>
      <div className="p-8 space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-navy">Digital Profiles</h1>
            <p className="text-sm text-gray-600 mt-1">
              You can create {maxProfiles} profile{maxProfiles > 1 ? 's' : ''} (1 free + 1 per delivered card)
            </p>
          </div>
          {canCreateMore && (
            <Button 
              onClick={() => navigate('/user/digital-profiles/create')}
              className="bg-navy text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Profile
            </Button>
          )}
        </div>

        {!canCreateMore && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-blue-800 text-sm">
              <strong>Profile Limit Reached:</strong> You've used all {maxProfiles} available profile slots. 
              Purchase and receive digital cards to unlock more profiles!
            </p>
          </div>
        )}

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-navy mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading profiles...</p>
          </div>
        ) : profiles.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-2xl shadow-sm">
            <User className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-700 mb-2">No Digital Profiles Yet</h3>
            <p className="text-gray-600 mb-6">Create your first digital profile to share online</p>
            <Button 
              onClick={() => navigate('/user/digital-profiles/create')}
              className="bg-navy text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Your First Profile
            </Button>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {profiles.map((profile) => (
              <div
                key={profile.id}
                className="bg-white rounded-2xl shadow-sm hover:shadow-md transition-shadow overflow-hidden"
              >
                {/* Cover Photo */}
                <div 
                  className="h-32 bg-gradient-to-r from-purple-600 to-pink-600"
                  style={{
                    backgroundImage: profile.cover_photo 
                      ? `url(${BACKEND_URL}${profile.cover_photo})`
                      : undefined,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                  }}
                />
                
                {/* Profile Content */}
                <div className="p-6 -mt-10">
                  {/* Profile Photo */}
                  <div className="mb-4">
                    {profile.profile_photo ? (
                      <img
                        src={`${BACKEND_URL}${profile.profile_photo}`}
                        alt={profile.profile_name}
                        className="w-20 h-20 rounded-full object-cover border-4 border-white shadow-lg"
                      />
                    ) : (
                      <div className="w-20 h-20 rounded-full bg-gray-200 flex items-center justify-center border-4 border-white shadow-lg">
                        <User className="w-10 h-10 text-gray-400" />
                      </div>
                    )}
                  </div>
                  
                  <h3 className="text-lg font-bold text-navy mb-1">{profile.profile_name}</h3>
                  <p className="text-sm text-gray-600 mb-4">@{profile.username}</p>
                  
                  <div className="flex gap-2">
                    <Button
                      onClick={() => navigate(`/user/digital-profiles/edit/${profile.id}`)}
                      variant="outline"
                      className="flex-1"
                    >
                      <Edit2 className="w-4 h-4 mr-2" />
                      Edit
                    </Button>
                    <Button
                      onClick={() => window.open(`/profile/${profile.username}`, '_blank')}
                      variant="outline"
                      className="flex-1"
                    >
                      <ExternalLink className="w-4 h-4 mr-2" />
                      View
                    </Button>
                    <Button
                      onClick={() => handleDelete(profile.id)}
                      variant="outline"
                      className="border-red-300 text-red-600"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                  
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <p className="text-xs text-gray-500">
                      Profile URL: <a href={`/profile/${profile.username}`} className="text-navy hover:underline" target="_blank" rel="noopener noreferrer">
                        {window.location.origin}/profile/{profile.username}
                      </a>
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </UserLayout>
  );
}
